import { formatVat } from './formatters';
import { toBig, addPercentage, type NumberLike } from './calc';
export type { NumberLike };

export const defaultVat = 25.5;
export const previousDefaultVats = [24];
export const foodVat = 13.5;
export const defaultVatFormatted = formatVat(defaultVat, false);
export const foodVatFormatted = formatVat(foodVat, false);
export const defaultSapVatCode = '5O';

export const hasVat = (value: NumberLike): boolean => {
  return +String(value).replace(',', '.') > 0 ? true : false;
};

/**
 * Extracts VAT from a value.
 *
 * @param {(number|string|Big)} value - The value to extract VAT from.
 * @param {(number|string|Big)} vat - The VAT percentage.
 * @param {(number|false)} [decimals=2] - The number of decimal places to round to. Defaults to 2, false to keep the original number of decimal places.
 * @returns {string} The VAT amount.
 */
export function extractVat(
  /**
   * The value to extract VAT from.
   */
  value: NumberLike,
  /**
   * The VAT percentage.
   */
  vat: NumberLike,
  /**
   * The number of decimal places to round to. Defaults to 2, false to keep the original number of decimal places.
   */
  decimals: number | false = 2
) {
  const val = toBig(value).minus(toBig(value).div(toBig(vat).div('100').plus('1')));
  return typeof decimals === 'number' && decimals > -1
    ? val.round(decimals).toFixed()
    : val.toFixed();
}

/**
 * Adds VAT to a given price.
 *
 * @returns {string} The result of adding VAT to the value, rounded to the specified number of decimal places.
 */
export function addVat(
  /**
   * The value to which VAT will be added.
   */
  value: NumberLike,
  /**
   * The VAT percentage to be added.
   */
  vat: NumberLike,
  /**
   * The number of decimal places to round to. Defaults to 2, false to keep the original number of decimal places.
   */
  decimals: number | false = 2
) {
  const val = toBig(addPercentage(value, vat));
  return typeof decimals === 'number' && decimals > -1
    ? val.round(decimals).toFixed()
    : val.toFixed();
}

/**
 * Subtracts VAT from a given price. I.e. vatless price.
 *
 * @returns {string} The result of subtracting VAT from the value, rounded to the specified number of decimal places.
 */
export function subtractVat(
  /**
   * The value from which VAT will be subtracted.
   */
  value: NumberLike,
  /**
   * The VAT percentage to be subtracted.
   */
  vat: NumberLike,
  /**
   * The number of decimal places to round to. Defaults to 2, false to keep the original number of decimal places.
   */
  decimals: number | false = 2
) {
  const val = toBig(value).minus(extractVat(value, vat, false));

  return typeof decimals === 'number' && decimals > -1
    ? val.round(decimals).toFixed()
    : val.toFixed();
}

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  defaultVat,
  previousDefaultVats,
  foodVat,
  defaultVatFormatted,
  foodVatFormatted,
  defaultSapVatCode,
  extractVat,
  addVat,
  subtractVat,
};
